<template>
  <div class="w-full p-4">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Expense Picker — Engagements</h2>
      <div class="text-sm text-gray-600">
        {{ enabledCount }} of {{ engagements.length }} enabled
      </div>
    </div>

    <div class="text-sm text-gray-600 mb-3">
      Tick an engagement to make it available in the expense form's engagement
      picker. Removing the tick takes it out of the picker but doesn't affect
      expenses already attributed to it. Paused / ended engagements are
      shown for context — you can include them in the picker if they are
      still actively accruing reimbursable expenses.
    </div>

    <div class="flex flex-row flex-wrap gap-3 mb-3 items-center text-sm">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search engagement, client..."
        class="p-1.5 border border-gray-400 rounded-md w-full sm:w-72"
      />
      <label class="flex items-center gap-1">
        <input type="checkbox" v-model="onlyEnabled" />
        Only show enabled
      </label>
      <span class="ml-auto text-gray-500">{{ filtered.length }} row<span v-if="filtered.length !== 1">s</span></span>
    </div>

    <div v-if="loading" class="p-6 text-center text-gray-500">Loading...</div>

    <div v-else class="border border-gray-400 rounded-md overflow-hidden">
      <table class="table-auto text-sm">
        <thead class="bg-dark-primary text-white">
          <tr>
            <th class="text-center px-2 py-2 w-16">In Picker</th>
            <th class="text-left px-2 py-2">Client</th>
            <th class="text-left px-2 py-2">Engagement</th>
            <th class="text-left px-2 py-2">Start Date</th>
            <th class="text-left px-2 py-2">End Date</th>
            <th class="text-left px-2 py-2">Status</th>
            <th class="w-full"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filtered.length === 0">
            <td colspan="7" class="px-2 py-4 text-center text-gray-500">No engagements match.</td>
          </tr>
          <tr
            v-for="e in filtered"
            :key="e.id"
            class="border-t border-gray-200"
            :class="e.is_in_picker ? '' : 'text-gray-500'"
          >
            <td class="px-2 py-2 text-center" @click.stop>
              <input
                type="checkbox"
                class="h-5 w-5 cursor-pointer accent-accent-primary"
                :checked="e.is_in_picker"
                :disabled="saving[e.id]"
                @change="toggle(e)"
              />
            </td>
            <td class="px-2 py-2 whitespace-nowrap">{{ e.client_name }}</td>
            <td class="px-2 py-2 whitespace-nowrap">{{ e.title }}</td>
            <td class="px-2 py-2 text-xs whitespace-nowrap">{{ e.start_date || '—' }}</td>
            <td class="px-2 py-2 text-xs whitespace-nowrap">{{ e.end_date || '—' }}</td>
            <td class="px-2 py-2 text-xs whitespace-nowrap">
              <span v-if="e.is_paused" class="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">paused</span>
              <span v-else-if="isEnded(e)" class="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">ended</span>
              <span v-else class="px-1.5 py-0.5 rounded bg-green-100 text-green-700">active</span>
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default { name: "engagement-picker-admin" };
</script>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { getEngagementPickerAdmin, toggleEngagementInPicker } from "@/services/ExpenseAdmin.js";

const store = useStore();
const $toast = useToast();

const loading = ref(true);
const engagements = ref([]);
const searchQuery = ref("");
const onlyEnabled = ref(false);
const saving = reactive({});

const enabledCount = computed(() => engagements.value.filter((e) => e.is_in_picker).length);

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return engagements.value.filter((e) => {
    if (onlyEnabled.value && !e.is_in_picker) return false;
    if (!q) return true;
    return (
      (e.title || "").toLowerCase().includes(q) ||
      (e.client_name || "").toLowerCase().includes(q)
    );
  });
});

function isEnded(e) {
  if (!e.end_date) return false;
  return e.end_date < todayISO();
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

async function load() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    engagements.value = (await getEngagementPickerAdmin(token)) || [];
  } catch (error) {
    $toast.open({ message: "Failed to load engagements", type: "error", duration: 3000 });
  }
  loading.value = false;
}

async function toggle(eng) {
  const previous = eng.is_in_picker;
  const next = !previous;
  // Optimistic update.
  eng.is_in_picker = next;
  saving[eng.id] = true;
  const token = store.state.auth.token;
  try {
    await toggleEngagementInPicker(token, eng.id, next);
  } catch (error) {
    eng.is_in_picker = previous;
    $toast.open({ message: "Toggle failed", type: "error", duration: 3000 });
  }
  saving[eng.id] = false;
}

onMounted(load);
</script>
